import { useMemo } from "react"
import PropTypes from "prop-types"
import { get, map } from "lodash"
import { Table, TableHead, TableRow, TableCell } from "@mui/material"
import ContributorsSorting from "components/contributors-sorting"
import ContributorsOptions from "components/contributors-options"
import ContributorsTableContent from "components/contributors-table-content"
import { sortData } from "helpers/contributorsSort"
import ContributorsTableHead from "components/contributors-table-head"
import { VIEW_TYPES } from "helpers/base"
import { METRIC_TYPES_SHAPE } from "helpers/metrics/shapes"
import { METRIC_TYPES } from "helpers/assets/constants"
import { getScaledPerformanceFormatter } from "helpers/analyze"
import { buildScaledCurrencyLabel } from "helpers/metrics/currency-scale-label"
import { numberFormatter } from "helpers/formatters/numbers"
import "./style.scss"

function getPerformanceTooltip(metricType) {
  return metricType === METRIC_TYPES.CURRENCY_PERFORMANCE ? "Performance" : "% Performance"
}

function getPerformanceLabel(metricType, currencyScale, currency) {
  return metricType === METRIC_TYPES.CURRENCY_PERFORMANCE ? buildScaledCurrencyLabel("Perf.", currencyScale, currency) : "% Perf."
}

function scaleDropdownValueColumns(columns, metricType, currencyScale, currency) {
  if (metricType !== METRIC_TYPES.CURRENCY_PERFORMANCE) {
    return columns
  }
  return map(columns, column => column.id === "value" ? { ...column, label: buildScaledCurrencyLabel(column.label, currencyScale, currency) } : column)
}

function ContributorsDetails({
  viewType,
  selectedValue,
  selected,
  hide,
  data,
  openSecurityProfile,
  openFactorProfile,
  openCompositeSecurity,
  sort,
  order,
  selectedGroup,
  selectedGroupSiblings,
  showGroup,
  hideGroup,
  setContributorsGrouping,
  contributorsGrouping,
  groupedData,
  availableRollups,
  contributorsSummaryText,
  csvData,
  selectedOption,
  selectedGroupValue,
  showNotHeld,
  toggleShowNotHeld,
  groupBy,
  details,
  show,
  groupOrder,
  groupsDropdownColumns,
  metricType,
  columnValues,
  currency,
}) {
  const showNotHeldToggle = viewType === VIEW_TYPES.ACTIVE ? toggleShowNotHeld : null
  const onBackClick = selectedGroup ? hideGroup : hide
  const selectedOptionColor = get(selectedOption, "color")
  const { formatter, currencyScale } = useMemo(() => getScaledPerformanceFormatter(metricType, columnValues), [metricType, columnValues])
  const dropdownItems = useMemo(() => selectedGroup ? selectedGroupSiblings : sortData(details, groupOrder), [selectedGroup, selectedGroupSiblings, details, groupOrder])
  const { formatter: dropdownFormatter, currencyScale: dropdownCurrencyScale } = useMemo(
    () => getScaledPerformanceFormatter(metricType, map(dropdownItems, "value")),
    [metricType, dropdownItems],
  )
  const dropdownColumns = useMemo(
    () => scaleDropdownValueColumns(groupsDropdownColumns, metricType, dropdownCurrencyScale, currency),
    [groupsDropdownColumns, metricType, dropdownCurrencyScale, currency],
  )

  return <div className="contributors-details-wrapper">
    <ContributorsOptions
      contributorsSummaryText={contributorsSummaryText}
      availableRollups={availableRollups}
      setGrouping={setContributorsGrouping}
      currentGrouping={contributorsGrouping}
      selectedGroup={selectedGroup}
      csvData={csvData}
      showNotHeld={showNotHeld}
      toggleShowNotHeld={showNotHeldToggle}
      groupBy={groupBy}
    />
    <Table className="contributors-details-table">
      <TableHead>
        <TableRow>
          <TableCell />
          <TableCell />
          <TableCell align="right" title={getPerformanceTooltip(metricType)}>{getPerformanceLabel(metricType, currencyScale, currency)}</TableCell>
        </TableRow>
        <TableRow className="sorting-row">
          <ContributorsSorting label="Name" id="name" order={order} sort={sort} />
          <ContributorsSorting
            align="right"
            infoText={`Modeled Average ${viewType === VIEW_TYPES.ABSOLUTE ? "% Equity" : "Active % Equity"}: Shows each modeled asset's share of the equity value used in model-based calculations.`}
            label={viewType === VIEW_TYPES.ABSOLUTE ? "Avg % Equity" : "Avg Active % Eq"}
            id="equity"
            order={order}
            sort={sort}
          />
          <ContributorsSorting align="right" label={viewType === VIEW_TYPES.ABSOLUTE ? "Absolute" : "Active"} id="viewType" order={order} sort={sort} />
        </TableRow>
        <TableRow>
          <ContributorsTableHead
            label={selectedGroup ? selectedGroup.name : selected.label}
            color={selectedOptionColor}
            onBackClick={onBackClick}
            dropdownColumns={dropdownColumns}
            dropdownItems={dropdownItems}
            onDropdownItemClick={(item) => selectedGroup ? showGroup(item) : show(item)}
            selectedItemId={selectedGroup ? selectedGroup.id : selected.id}
            formatter={dropdownFormatter}
          />
          <TableCell align="right" style={{ color: selectedOptionColor }}>{selectedGroup ? numberFormatter(selectedGroup.totalPercentEquity * 100) : "-"}</TableCell>
          <TableCell align="right" style={{ color: selectedOptionColor }}>{formatter(selectedGroup ? selectedGroupValue : selectedValue)}</TableCell>
        </TableRow>
      </TableHead>
      <ContributorsTableContent
        selected={selected}
        openSecurityProfile={openSecurityProfile}
        openFactorProfile={openFactorProfile}
        openCompositeSecurity={openCompositeSecurity}
        data={data}
        contributorsGrouping={contributorsGrouping}
        selectedGroup={selectedGroup}
        showGroup={showGroup}
        groupedData={groupedData}
        formatter={formatter}
      />
    </Table>
  </div>
}

ContributorsDetails.propTypes = {
  selected: PropTypes.object.isRequired,
  selectedValue: PropTypes.number.isRequired,
  data: PropTypes.array.isRequired,
  viewType: PropTypes.string.isRequired,
  order: PropTypes.object.isRequired,
  hide: PropTypes.func.isRequired,
  sort: PropTypes.func.isRequired,
  openSecurityProfile: PropTypes.func.isRequired,
  openFactorProfile: PropTypes.func.isRequired,
  openCompositeSecurity: PropTypes.func.isRequired,
  selectedGroup: PropTypes.object,
  selectedGroupSiblings: PropTypes.array,
  showGroup: PropTypes.func.isRequired,
  hideGroup: PropTypes.func.isRequired,
  setContributorsGrouping: PropTypes.func.isRequired,
  contributorsGrouping: PropTypes.object,
  groupedData: PropTypes.array.isRequired,
  availableRollups: PropTypes.array.isRequired,
  contributorsSummaryText: PropTypes.string.isRequired,
  csvData: PropTypes.object.isRequired,
  selectedOption: PropTypes.object.isRequired,
  selectedGroupValue: PropTypes.number,
  showNotHeld: PropTypes.bool.isRequired,
  toggleShowNotHeld: PropTypes.func.isRequired,
  groupBy: PropTypes.string,
  details: PropTypes.array.isRequired,
  show: PropTypes.func.isRequired,
  groupOrder: PropTypes.object.isRequired,
  groupsDropdownColumns: PropTypes.array.isRequired,
  tagCategories: PropTypes.array,
  metricType: METRIC_TYPES_SHAPE,
  columnValues: PropTypes.array.isRequired,
  currency: PropTypes.string,
}

export default ContributorsDetails
